// OperationFilters.tsx
import React, { useState } from 'react';
import {
  Row,
  Col,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  Card,
  Collapse,
  Badge
} from 'antd';
import {
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OperationType } from './OperationTypeIcon';
import { OperationTypeIcon, getOperationTypeLabel } from './OperationTypeIcon';
import { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

export interface OperationFilterValues {
  type?: OperationType[];
  status?: string[];
  username?: string;
  dateRange?: [Dayjs, Dayjs];
  amountMin?: number;
  amountMax?: number;
  linkedTransferId?: number;
}

interface OperationFiltersProps {
  onFilterChange: (filters: OperationFilterValues) => void;
  onClearFilters: () => void;
  activeFilters?: OperationFilterValues;
}

export const OperationFilters: React.FC<OperationFiltersProps> = ({
  onFilterChange,
  onClearFilters,
  activeFilters = {}
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<OperationFilterValues>(activeFilters);

  const operationTypes: OperationType[] = ['CREATE_USER', 'DEPOSIT', 'WITHDRAW', 'BALANCE_CHECK'];
  const statusOptions = ['COMPLETED', 'PROCESSING', 'PENDING', 'FAILED'];

  const handleFilterChange = (key: keyof OperationFilterValues, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleClearAllFilters = () => {
    setLocalFilters({});
    onClearFilters();
  };

  // Count active filters
  const activeFilterCount = Object.values(localFilters).filter(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (value !== undefined && value !== null && value !== '') return true;
    return false;
  }).length;

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: '12px 16px' }}
    >
      <Collapse
        ghost
        expandIconPosition="end"
        items={[
          {
            key: '1',
            label: (
              <Space>
                <Badge count={activeFilterCount} offset={[8, 0]}>
                  <FilterOutlined style={{ fontSize: 16 }} />
                </Badge>
                <span style={{ fontWeight: 500 }}>
                  {t('common.filters') || 'Filtros Avanzados'}
                </span>
              </Space>
            ),
            children: (
              <div>
                <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                  {/* Tipo de Operación */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div style={{ marginBottom: 4 }}>
                      <Space size={4}>
                        <FilterOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Tipo de Operación
                        </span>
                      </Space>
                    </div>
                    <Select
                      mode="multiple"
                      placeholder="Todos los tipos"
                      style={{ width: '100%' }}
                      value={localFilters.type}
                      onChange={(value) => handleFilterChange('type', value)}
                      allowClear
                      maxTagCount="responsive"
                    >
                      {operationTypes.map((type) => (
                        <Select.Option key={type} value={type}>
                          <Space size="small">
                            <OperationTypeIcon type={type} size="small" />
                            <span>{getOperationTypeLabel(type)}</span>
                          </Space>
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>

                  {/* Estado */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div style={{ marginBottom: 4 }}>
                      <Space size={4}>
                        <FilterOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Estado
                        </span>
                      </Space>
                    </div>
                    <Select
                      mode="multiple"
                      placeholder="Todos los estados"
                      style={{ width: '100%' }}
                      value={localFilters.status}
                      onChange={(value) => handleFilterChange('status', value)}
                      allowClear
                      maxTagCount="responsive"
                    >
                      {statusOptions.map((status) => (
                        <Select.Option key={status} value={status}>
                          <Tag
                            color={
                              status === 'COMPLETED'
                                ? 'success'
                                : status === 'PROCESSING'
                                ? 'processing'
                                : status === 'FAILED'
                                ? 'error'
                                : 'default'
                            }
                          >
                            {status}
                          </Tag>
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>

                  {/* Usuario */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div style={{ marginBottom: 4 }}>
                      <Space size={4}>
                        <UserOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Usuario
                        </span>
                      </Space>
                    </div>
                    <Input
                      placeholder="Buscar por username"
                      prefix={<SearchOutlined />}
                      value={localFilters.username}
                      onChange={(e) => handleFilterChange('username', e.target.value)}
                      allowClear
                    />
                  </Col>

                  {/* Rango de Fechas */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div style={{ marginBottom: 4 }}>
                      <Space size={4}>
                        <CalendarOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Rango de Fechas
                        </span>
                      </Space>
                    </div>
                    <RangePicker
                      style={{ width: '100%' }}
                      value={localFilters.dateRange}
                      onChange={(dates) => handleFilterChange('dateRange', dates)}
                      format="DD/MM/YYYY"
                      placeholder={['Fecha inicio', 'Fecha fin']}
                    />
                  </Col>

                  {/* Monto Mínimo */}
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <div style={{ marginBottom: 4 }}>
                      <Space size={4}>
                        <DollarOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Monto Min.
                        </span>
                      </Space>
                    </div>
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Min"
                      value={localFilters.amountMin}
                      onChange={(value) => handleFilterChange('amountMin', value)}
                      min={0}
                      prefix="$"
                    />
                  </Col>

                  {/* Monto Máximo */}
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <div style={{ marginBottom: 4 }}>
                      <Space size={4}>
                        <DollarOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Monto Max.
                        </span>
                      </Space>
                    </div>
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Max"
                      value={localFilters.amountMax}
                      onChange={(value) => handleFilterChange('amountMax', value)}
                      min={localFilters.amountMin || 0}
                      prefix="$"
                    />
                  </Col>

                  {/* Transfer ID */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div style={{ marginBottom: 4 }}>
                      <Space size={4}>
                        <SearchOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Transfer ID
                        </span>
                      </Space>
                    </div>
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="ID de transferencia"
                      value={localFilters.linkedTransferId}
                      onChange={(value) => handleFilterChange('linkedTransferId', value)}
                      min={1}
                      prefix="#"
                    />
                  </Col>
                </Row>

                {/* Action Buttons */}
                <Row style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                      <Button
                        icon={<ClearOutlined />}
                        onClick={handleClearAllFilters}
                        disabled={activeFilterCount === 0}
                      >
                        Limpiar Filtros
                      </Button>
                      <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleApplyFilters}
                      >
                        Aplicar Filtros
                      </Button>
                    </Space>
                  </Col>
                </Row>

                {/* Active Filters Tags */}
                {activeFilterCount > 0 && (
                  <Row style={{ marginTop: 12 }}>
                    <Col span={24}>
                      <Space size={[8, 8]} wrap>
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Filtros activos:
                        </span>
                        {localFilters.type && localFilters.type.length > 0 && (
                          <Tag closable onClose={() => handleFilterChange('type', [])}>
                            Tipos: {localFilters.type.length}
                          </Tag>
                        )}
                        {localFilters.status && localFilters.status.length > 0 && (
                          <Tag closable onClose={() => handleFilterChange('status', [])}>
                            Estados: {localFilters.status.length}
                          </Tag>
                        )}
                        {localFilters.username && (
                          <Tag closable onClose={() => handleFilterChange('username', undefined)}>
                            Usuario: {localFilters.username}
                          </Tag>
                        )}
                        {localFilters.dateRange && (
                          <Tag closable onClose={() => handleFilterChange('dateRange', undefined)}>
                            Fechas: {localFilters.dateRange[0].format('DD/MM')} - {localFilters.dateRange[1].format('DD/MM')}
                          </Tag>
                        )}
                        {(localFilters.amountMin !== undefined || localFilters.amountMax !== undefined) && (
                          <Tag closable onClose={() => {
                            handleFilterChange('amountMin', undefined);
                            handleFilterChange('amountMax', undefined);
                          }}>
                            Monto: ${localFilters.amountMin || 0} - ${localFilters.amountMax || '∞'}
                          </Tag>
                        )}
                        {localFilters.linkedTransferId && (
                          <Tag closable onClose={() => handleFilterChange('linkedTransferId', undefined)}>
                            Transfer: #{localFilters.linkedTransferId}
                          </Tag>
                        )}
                      </Space>
                    </Col>
                  </Row>
                )}
              </div>
            )
          }
        ]}
      />
    </Card>
  );
};
